/**
 * DataTable Component
 * Generic reusable table with pagination, sorting, actions, i18n support,
 * and integrated toolbar with search, filters, and column configuration
 */

import React, { useMemo, useCallback, useState, useEffect, useRef } from 'react';
import { Table } from 'antd';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import type { SorterResult } from 'antd/es/table/interface';
import filter from 'lodash/filter';
import map from 'lodash/map';

import { useUIColors, CHECKBOX_BOX_SIZE } from '../../theme';
import { useUIText } from '../../i18n';
import { TableToolbar } from './TableToolbar';
import { buildColumnFilterProps } from './ColumnFilterDropdown';
import { applyFilters } from '../../filtering';
import type { DataTableProps, ColumnConfig, FilterField, FilterValues } from './DataTable.types';

/** Stable empty object so `filterValues ?? EMPTY_FILTER_VALUES` never creates a new reference. */
const EMPTY_FILTER_VALUES: FilterValues = {};

/** localStorage key prefix for column configuration */
const COLUMN_CONFIG_KEY_PREFIX = 'ach_columns_';

/**
 * EAA-1839: gutter either side of the checkbox in a `compactSelectionColumn`.
 * Matches the sheet's own `paddingXS` token (`antdTheme.ts`'s `token.paddingXS:
 * 8`) — the same inset antd's own selection-column stylesheet already applies,
 * so a caller who takes the derived default gets a column whose padding and
 * width agree instead of a second, unrelated number.
 */
const SELECTION_COLUMN_GUTTER = 8;

/** Get column config from localStorage */
export function getStoredColumns(key: string): string[] | null {
    try {
        const stored = localStorage.getItem(`${COLUMN_CONFIG_KEY_PREFIX}${key}`);
        if (stored) {
            const parsed = JSON.parse(stored);
            return parsed.visibleColumns || null;
        }
    } catch {
        // Ignore parse errors
    }
    return null;
}

/** Save column config to localStorage */
export function saveStoredColumns(key: string, visibleColumns: string[]): void {
    try {
        localStorage.setItem(
            `${COLUMN_CONFIG_KEY_PREFIX}${key}`,
            JSON.stringify({ visibleColumns, timestamp: Date.now() })
        );
    } catch {
        // Ignore storage errors
    }
}

export function DataTable<T extends object>({
    data,
    columns,
    pagination,
    loading = false,
    rowKey,
    onPageChange,
    onSortChange,
    sortedInfo,
    actions,
    actionsWidth = 160,
    scroll = { x: 900 },
    emptyText,
    showSizeChanger = true,
    pageSizeOptions = ['10', '20', '50'],
    clientPageSize,
    virtual = false,
    rowSelection,
    compactSelectionColumn,
    expandable,
    rowClassName,
    onRowClick,
    className,
    // Toolbar props
    showToolbar = false,
    searchValue,
    searchPlaceholder,
    onSearchChange,
    filterFields,
    filterValues,
    onFilterChange,
    onReset,
    onRefresh,
    searchDebounce,
    toolbarExtra,
    // Column configuration props
    columnConfigKey,
    defaultVisibleColumns,
    visibleColumns: visibleColumnsProp,
    onVisibleColumnsChange,
    showSorterTooltip = false,
    styles,
    filterMode = 'server',
    searchKeys,
}: DataTableProps<T>): React.ReactElement {
    const { common } = useUIText();
    const colors = useUIColors();

    const columnsRef = useRef(columns);
    columnsRef.current = columns;

    const paginationRef = useRef(pagination);
    paginationRef.current = pagination;

    const computedDefaultVisibleColumns = useMemo(() => {
        if (defaultVisibleColumns && defaultVisibleColumns.length > 0) {
            return defaultVisibleColumns;
        }
        return map(columns, 'key');
    }, [defaultVisibleColumns, columns]);

    const isColumnControlled =
        visibleColumnsProp !== undefined && onVisibleColumnsChange !== undefined;

    const [internalVisibleColumns, setInternalVisibleColumns] = useState<string[]>(() => {
        if (columnConfigKey) {
            const stored = getStoredColumns(columnConfigKey);
            if (stored) {
                return stored;
            }
        }
        return computedDefaultVisibleColumns;
    });

    const visibleColumns = isColumnControlled ? visibleColumnsProp : internalVisibleColumns;

    // Avoid setState when only the columns array reference changed but keys
    // are unchanged (prevents extra Table body/cell churn).
    useEffect(() => {
        if (!columnConfigKey && !isColumnControlled) {
            setInternalVisibleColumns((prev) => {
                const next = computedDefaultVisibleColumns;
                if (prev.length === next.length && prev.every((key, index) => key === next[index])) {
                    return prev;
                }
                return next;
            });
        }
    }, [computedDefaultVisibleColumns, columnConfigKey, isColumnControlled]);

    const handleVisibleColumnsChange = useCallback(
        (keys: string[]) => {
            if (columnConfigKey) {
                saveStoredColumns(columnConfigKey, keys);
            }
            if (isColumnControlled) {
                onVisibleColumnsChange?.(keys);
            } else {
                setInternalVisibleColumns(keys);
            }
        },
        [columnConfigKey, isColumnControlled, onVisibleColumnsChange]
    );

    const visibleColumnConfigs = useMemo(() => {
        return filter(columns, (col) => visibleColumns.includes(col.key));
    }, [columns, visibleColumns]);

    // EAA-1666: `col.filter` is sugar for an entry in the legacy `filterFields`
    // prop. Derive one FilterField per column that declares it, but let an
    // explicit `filterFields` entry with the same key win — back-compat
    // safety net if a caller already declared that key before this feature.
    const legacyFilterFieldKeys = useMemo(() => new Set(map(filterFields, 'key')), [filterFields]);

    const columnDrivenFilterFields = useMemo<FilterField[]>(() => {
        const derived: FilterField[] = [];
        columns.forEach((col) => {
            if (!col.filter) return;
            const key = col.filterKey ?? col.key;
            if (legacyFilterFieldKeys.has(key)) return;
            derived.push({ ...col.filter, key, columnKey: col.key });
        });
        return derived;
    }, [columns, legacyFilterFieldKeys]);

    const effectiveFilterValues = filterValues ?? EMPTY_FILTER_VALUES;

    const antColumns: ColumnsType<T> = useMemo(() => {
        const cols: ColumnsType<T> = visibleColumnConfigs.map((col: ColumnConfig<T>) => ({
            key: col.key,
            title: col.title,
            dataIndex: col.dataIndex as string,
            width: col.width,
            align: col.align,
            ellipsis: col.ellipsis,
            render: col.render,
            sorter: col.sorter,
            sortOrder:
                sortedInfo?.field === (col.sortKey || col.dataIndex || col.key)
                    ? sortedInfo.order
                    : null,
            // EAA-1666: additive — `{}` for every column without `.filter`,
            // so pre-existing columns are byte-for-byte unaffected.
            ...buildColumnFilterProps({
                col,
                filterValues: effectiveFilterValues,
                onFilterChange,
                iconColors: { active: colors.primary, inactive: colors.textMuted },
                texts: { all: common.all, startDate: common.startDate, endDate: common.endDate },
                resetLabel: common.resetFilters,
                confirmLabel: common.confirm,
            }),
        }));

        if (actions) {
            cols.push({
                key: 'actions',
                /**
                 * EAA-1817: the header is a screen-reader-only label, not printed
                 * text.
                 *
                 * It used to print `common.actions` ("Thao tác"), which fought its
                 * own `actionsWidth`: a caller narrowing the gutter to fit a
                 * single kebab (44px is the design's own width for a row-menu
                 * column) got the word broken one CHARACTER per line, and the
                 * header row grew to ~200px to hold it. The design's data-table
                 * standard (frame 97) draws this column's header EMPTY, because a
                 * gutter of row menus needs no name — the control in each cell is
                 * already labelled.
                 *
                 * The name is still emitted for assistive tech, so the column is
                 * not anonymous to a screen reader.
                 */
                title: (
                    <span
                        style={{
                            position: 'absolute',
                            width: 1,
                            height: 1,
                            padding: 0,
                            margin: -1,
                            overflow: 'hidden',
                            clip: 'rect(0 0 0 0)',
                            whiteSpace: 'nowrap',
                            border: 0,
                        }}
                    >
                        {common.actions}
                    </span>
                ),
                width: actionsWidth,
                align: 'center',
                fixed: 'right',
                render: (_, record) => actions(record),
            });
        }

        return cols;
    }, [
        visibleColumnConfigs,
        actions,
        actionsWidth,
        common.actions,
        common.all,
        common.startDate,
        common.endDate,
        common.resetFilters,
        common.confirm,
        sortedInfo,
        effectiveFilterValues,
        onFilterChange,
        colors.primary,
        colors.textMuted,
    ]);

    const showPaginationTotal = useCallback(
        (total: number, range: [number, number]) =>
            common.showing
                .replace('{{from}}', String(range[0]))
                .replace('{{to}}', String(range[1]))
                .replace('{{total}}', String(total)),
        [common.showing]
    );

    const handlePaginationChange = useCallback(
        (page: number, pageSize: number) => {
            onPageChange?.(page, pageSize);
        },
        [onPageChange]
    );

    const tablePagination: TablePaginationConfig | false = useMemo(() => {
        if (pagination) {
            return {
                current: pagination.page,
                pageSize: pagination.pageSize,
                total: pagination.total,
                showSizeChanger,
                pageSizeOptions,
                showTotal: showPaginationTotal,
                onChange: handlePaginationChange,
            };
        }
        // EAA-1738: client-side pagination — no `current`/`total`/`onChange` so
        // Ant Design owns the page state and slices `data` internally, keeping
        // only one page of rows mounted per commit (was rendering all rows).
        if (clientPageSize) {
            return {
                pageSize: clientPageSize,
                showSizeChanger,
                pageSizeOptions,
                showTotal: showPaginationTotal,
            };
        }
        return false;
    }, [
        pagination,
        clientPageSize,
        showSizeChanger,
        pageSizeOptions,
        showPaginationTotal,
        handlePaginationChange,
    ]);

    const getRowKey = useCallback(
        (record: T): string => {
            if (typeof rowKey === 'function') {
                return rowKey(record);
            }
            return String(record[rowKey]);
        },
        [rowKey]
    );

    const handleTableChange = useCallback(
        (
            newPagination: TablePaginationConfig,
            _filters: unknown,
            sorter: SorterResult<T> | SorterResult<T>[]
        ) => {
            const p = paginationRef.current;
            if (onPageChange && p && newPagination) {
                const newPage = newPagination.current ?? p.page;
                const newPageSize = newPagination.pageSize ?? p.pageSize;
                if (newPage !== p.page || newPageSize !== p.pageSize) {
                    onPageChange(newPage, newPageSize);
                }
            }
            if (onSortChange) {
                const sortResult = Array.isArray(sorter) ? sorter[0] : sorter;
                const field = (sortResult.columnKey as string) || (sortResult.field as string);

                const column = columnsRef.current.find(
                    (col) => col.key === field || col.dataIndex === field
                );
                const sortKey = column?.sortKey || field;

                if (sortResult.order) {
                    const ordering = sortResult.order === 'descend' ? `-${sortKey}` : sortKey;
                    onSortChange(ordering);
                } else {
                    onSortChange('');
                }
            }
        },
        [onPageChange, onSortChange]
    );

    /**
     * EAA-1843: opt-in client filtering, through the same engine `MatrixTable`
     * uses. `'server'` returns `data` untouched, so the default path is exactly
     * what it was.
     */
    const visibleData = useMemo(() => {
        if (filterMode !== 'client') return data;
        const fields = [...(filterFields ?? []), ...columnDrivenFilterFields];
        const keys =
            searchKeys ??
            columns.map((c) => String(c.dataIndex ?? c.key)).filter((k): k is string => Boolean(k));
        return applyFilters(data, fields, effectiveFilterValues, {
            search: searchValue,
            searchKeys: keys,
        });
    }, [
        filterMode,
        data,
        filterFields,
        columnDrivenFilterFields,
        effectiveFilterValues,
        searchValue,
        searchKeys,
        columns,
    ]);

    const tableLocale = useMemo(
        () => ({ emptyText: emptyText || common.noData }),
        [emptyText, common.noData]
    );

    // EAA-1817: was 16 — off the design system's own radius scale (which tops
    // out at 12 for a whole frame; 8 is the documented "cards/inputs" step,
    // see `createAntdTheme`'s `SHEET.radius` comment). It also silently
    // disagreed with this same table's OWN header corners, themed via
    // `Table.headerBorderRadius: SHEET.radius` (8) — the header's rounded top
    // and the container's rounded top were never the same radius.
    const tableSurfaceStyle = useMemo(
        () => ({ background: colors.surface, borderRadius: 8 }),
        [colors.surface]
    );

    const containerCls = className ? `data-table-container ${className}` : 'data-table-container';

    /**
     * EAA-1839: `compactSelectionColumn` only ever ADDS `columnWidth` to a
     * `rowSelection` the caller already gave us — it never fabricates one, so
     * a table with no `rowSelection` stays exactly as it renders today. A
     * caller-set `columnWidth` always wins: this is a default, not an
     * override of an explicit choice.
     */
    const effectiveRowSelection = useMemo(() => {
        if (!rowSelection || !compactSelectionColumn) return rowSelection;
        if (rowSelection.columnWidth !== undefined) return rowSelection;
        const width =
            typeof compactSelectionColumn === 'number'
                ? compactSelectionColumn
                : CHECKBOX_BOX_SIZE + SELECTION_COLUMN_GUTTER * 2;
        return { ...rowSelection, columnWidth: width };
    }, [rowSelection, compactSelectionColumn]);

    /**
     * EAA-1839: turn the narrow `onRowClick` into antd's `onRow`. `undefined`
     * when the prop is absent, so antd receives exactly what it received
     * before this feature existed rather than a no-op handler on every row.
     */
    const handleRow = useMemo(() => {
        if (!onRowClick) return undefined;
        return (record: T) => ({
            onClick: (event: React.MouseEvent<HTMLElement>) => onRowClick(record, event),
        });
    }, [onRowClick]);

    /**
     * EAA-1839: the styling hook for a clickable row. This is a CLASS, not an
     * inline cursor/background, because `packages/ui` ships no CSS and the
     * hover colour is the app's decision — see `onRowClick`'s doc. Composed
     * with the caller's own `rowClassName` so a per-row tone survives.
     */
    const effectiveRowClassName = useMemo(() => {
        if (!onRowClick) return rowClassName;
        return (record: T, index: number): string => {
            const own =
                typeof rowClassName === 'function'
                    ? rowClassName(record, index)
                    : (rowClassName ?? '');
            return own ? `${own} data-table-row--clickable` : 'data-table-row--clickable';
        };
    }, [onRowClick, rowClassName]);

    return (
        <div className={containerCls}>
            {showToolbar && (
                <TableToolbar
                    columns={columns as ColumnConfig<unknown>[]}
                    visibleColumns={visibleColumns}
                    onVisibleColumnsChange={handleVisibleColumnsChange}
                    defaultVisibleColumns={computedDefaultVisibleColumns}
                    searchValue={searchValue}
                    searchPlaceholder={searchPlaceholder}
                    onSearchChange={onSearchChange}
                    filterFields={filterFields}
                    columnDrivenFilterFields={columnDrivenFilterFields}
                    filterValues={filterValues}
                    onFilterChange={onFilterChange}
                    onReset={onReset}
                    onRefresh={onRefresh}
                    loading={loading}
                    searchDebounce={searchDebounce}
                    extra={toolbarExtra}
                />
            )}

            <Table<T>
                className="data-table"
                columns={antColumns}
                dataSource={visibleData}
                rowKey={getRowKey}
                loading={loading}
                virtual={virtual}
                pagination={tablePagination}
                scroll={scroll}
                onChange={handleTableChange}
                rowSelection={effectiveRowSelection}
                expandable={expandable}
                rowClassName={effectiveRowClassName}
                onRow={handleRow}
                showSorterTooltip={showSorterTooltip}
                locale={tableLocale}
                style={tableSurfaceStyle}
                styles={styles}
            />
        </div>
    );
}

export default DataTable;
